/**
 * FloatingPanel — 강의 화면의 떠 있는 판(개념·라벨 고르기, 공유 범위, 줄·강의 더보기, 강의 바꾸기).
 *
 * 저장소에 공용 팝오버가 없어(조사 2026-09-13) 이미 화면에서 검증된 두 규칙을 합쳤다:
 *   - 시험지 폴더 메뉴(ExamPapersV2/FolderTree.jsx FolderMenu) — body 포털 + fixed, 여는 버튼 아래(모자라면 위로 뒤집기),
 *     바깥 mousedown·창 크기 변경·바깥 스크롤에 닫힌다. 나무처럼 세로로 스크롤하는 상자 안에서도 잘리지 않는다.
 *   - 공통 Modal(common/Modal.js) — 열면 안의 첫 입력(없으면 판)에 초점, 닫으면 **연 버튼으로 초점을 돌려준다**(설계 §W02).
 * 폴더 메뉴와 다른 점 둘: 판 **안의** 스크롤로는 닫지 않고(후보 목록을 굴리다 닫히지 않게), 여는 버튼 누름은 바깥으로 치지
 * 않는다(버튼의 onClick 이 여닫기를 맡는다 — 바깥 판정이 먼저 닫고 onClick 이 다시 여는 깜빡임을 막는다).
 *
 * Escape 는 판의 onKeyDown 에서 받고 전파를 멈춘다 — 시험지 조립 창·강의 덮개도 Escape 로 닫히므로, 판만 닫아야 한다.
 * 안쪽 부품이 Escape 를 먼저 쓰려면 e.preventDefault() 하면 된다(예: 한글 조합 중).
 *
 * 층: z-index.css 의 변수는 로드되지 않아 auto 로 떨어진다(메모 「z-index 변수가 죽어 있다」) — 숫자로 준다.
 * 시험지 조립 창(9999970)과 기본 모달(9999980) 사이. 하단 시트는 모바일 하단 내비(2147483647) 위에 서야 해서 같은 값에 늦게 붙인다.
 */
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SyllabusModalContext } from './SyllabusTreeDrawer';

export const FLOATING_Z = 9999975;
export const SHEET_Z = 2147483647;

const FOCUSABLE = 'input:not([disabled]),textarea:not([disabled]),select:not([disabled]),button:not([disabled]),[href],[tabindex]:not([tabindex="-1"])';

/**
 * props
 *   anchorRef  — 여는 버튼(자리 계산·초점 복귀·바깥 판정 제외). sheet 이면 자리 계산에 쓰지 않는다
 *   onClose(reason) — 'outside' | 'escape' | 'resize' | 'scroll'
 *   align      — 'start'(버튼 왼쪽에 판 왼쪽) | 'end'(버튼 오른쪽에 판 오른쪽)
 *   sheet      — 좁은 화면(767px 이하): 아래에서 올라오는 시트로 연다(설계 §W08)
 *   label      — 판의 이름(스크린리더)
 *   initialFocus — false 면 초점을 옮기지 않는다(메뉴처럼 첫 항목이 버튼뿐일 때도 옮기는 게 기본)
 *   className  — 폭 등(w-72 …)
 */
function FloatingPanel({
  anchorRef = null,
  onClose,
  align = 'start',
  sheet = false,
  label = undefined,
  initialFocus = true,
  className = '',
  children,
}) {
  const boxRef = useRef(null);
  const closeRef = useRef(onClose);
  const [pos, setPos] = useState(null);
  const insideModal = useContext(SyllabusModalContext);
  const trapsFocus = sheet || insideModal;
  closeRef.current = onClose;

  // 연 순간의 초점(대개 여는 버튼)과 여는 버튼을 붙잡아 두었다가 닫힐 때 돌려준다.
  // 개발 모드(StrictMode)는 효과를 한 번 걷었다 다시 세운다 — 그때 걸어 둔 복귀 예약이 판 안으로 옮긴 초점을 뒤에서 덮지 않게,
  // 다시 설 때 예약을 지우고, 예약이 돌 때도 판이 정말 사라졌는지(boxRef 가 비었는지) 한 번 더 본다.
  const restoreTimer = useRef(null);
  useLayoutEffect(() => {
    if (restoreTimer.current) { clearTimeout(restoreTimer.current); restoreTimer.current = null; }
    const opener = document.activeElement;
    const anchorEl = anchorRef?.current || null;
    return () => {
      const prev = anchorEl || opener;
      // 닫는 클릭의 기본 동작이 끝난 뒤 돌려준다 — 곧 사라질 요소에 초점이 붙지 않게
      restoreTimer.current = setTimeout(() => {
        restoreTimer.current = null;
        if (prev instanceof HTMLElement && document.contains(prev)) prev.focus();
      }, 0);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 자리 — 처음 한 번 재고, 내용 크기가 바뀌면(후보 목록이 늘면) 크기 감시로 다시 잰다. 같은 값이면 그대로 둔다
  useLayoutEffect(() => {
    if (sheet) return undefined;
    const measure = () => {
      const el = boxRef.current;
      const a = anchorRef?.current;
      if (!el || !a) return;
      const rect = a.getBoundingClientRect();
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const raw = align === 'end' ? rect.right - w : rect.left;
      const left = Math.max(8, Math.min(raw, window.innerWidth - w - 8));
      const below = rect.bottom + 6;
      const want = below + h <= window.innerHeight - 8 ? below : rect.top - h - 6;
      const top = Math.max(8, Math.min(want, window.innerHeight - h - 8));
      setPos((p) => (p && p.left === left && p.top === top ? p : { left, top }));
    };
    measure();
    if (typeof ResizeObserver === 'undefined' || !boxRef.current) return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(boxRef.current);
    return () => ro.disconnect();
  }, [sheet, align, anchorRef]);

  // 처음 보일 때 초점을 판 안으로
  const shown = sheet || pos != null;
  useLayoutEffect(() => {
    if (!shown || !initialFocus) return;
    const el = boxRef.current;
    if (!el || el.contains(document.activeElement)) return;
    const first = el.querySelector(FOCUSABLE);
    (first || el).focus();
  }, [shown]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const inside = (t) => (boxRef.current && boxRef.current.contains(t)) || (anchorRef?.current && anchorRef.current.contains(t));
    const onDown = (e) => { if (!inside(e.target)) closeRef.current('outside'); };
    const onResize = () => closeRef.current('resize');
    const onScroll = (e) => {
      if (sheet) return;
      if (boxRef.current && e.target instanceof Node && boxRef.current.contains(e.target)) return;
      closeRef.current('scroll');
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [sheet]); // eslint-disable-line react-hooks/exhaustive-deps

  const onKeyDown = (e) => {
    // 하단 시트는 화면을 덮는다(aria-modal) — Tab 이 뒤 화면으로 새지 않게 판 안에서 돈다(설계 §W08)
    if (trapsFocus && e.key === 'Tab' && boxRef.current) {
      const list = Array.from(boxRef.current.querySelectorAll(FOCUSABLE));
      if (list.length) {
        const first = list[0];
        const last = list[list.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === boxRef.current)) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      } else { e.preventDefault(); boxRef.current.focus(); }
      return;
    }
    if (e.key !== 'Escape' || e.defaultPrevented || e.nativeEvent?.isComposing) return;
    e.stopPropagation();
    closeRef.current('escape');
  };

  if (sheet) {
    return createPortal(
      <div style={{ zIndex: SHEET_Z }} className="fixed inset-0 flex flex-col justify-end">
        {/* 뒤를 누르면 닫는다 — 시트 밖 조작을 막아 초점이 뒤로 새지 않게 */}
        <div aria-hidden="true" className="absolute inset-0 bg-black/20" />
        <div
          ref={boxRef}
          role="dialog"
          aria-modal="true"
          aria-label={label}
          tabIndex={-1}
          onKeyDown={onKeyDown}
          className={`relative max-h-[80vh] overflow-y-auto rounded-t-2xl border border-gray-200 bg-white p-3 shadow-2xl focus:outline-none ${className}`}
        >
          {children}
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      ref={boxRef}
      role="dialog"
      aria-modal={insideModal ? 'true' : 'false'}
      aria-label={label}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className={`fixed rounded-2xl border border-gray-200 bg-white p-1.5 shadow-2xl focus:outline-none ${className}`}
      style={{
        zIndex: FLOATING_Z,
        left: pos ? pos.left : -9999,
        top: pos ? pos.top : -9999,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

export default FloatingPanel;
